import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import useSession from '../hooks/useSession';
import styled from 'styled-components';
import useFrame from '../hooks/useFrame';
import { useWindowSize } from 'react-hooks-window-size';
import { CircularProgress } from '@material-ui/core';

interface Props {
  video: string;
  index: number;
  apikey: string;
  baseUrl?: string;
  frame?: number;
  interactive: boolean;
  size: number;
  framerate?: number;
  setFrame: (index: number, frame: number) => void;
}

const PIXELS_PER_FRAME = 1;

type CanvasRenderingContext2DExtra = CanvasRenderingContext2D & {
  transformedPoint: (x: number, y: number) => DOMPoint;
};

const scaleFactor = 1.1;

function trackTransforms(ctx: any) {
  var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  var xform = svg.createSVGMatrix();
  ctx.getTransform = function () {
    return xform;
  };

  var savedTransforms: Array<any> = [];
  var save = ctx.save;
  ctx.save = function () {
    savedTransforms.push(xform.translate(0, 0));
    return save.call(ctx);
  };
  var restore = ctx.restore;
  ctx.restore = function () {
    xform = savedTransforms.pop();
    return restore.call(ctx);
  };

  var scale = ctx.scale;
  ctx.scale = function (sx: number, sy: number) {
    xform = xform.scaleNonUniform(sx, sy);
    return scale.call(ctx, sx, sy);
  };
  var rotate = ctx.rotate;
  ctx.rotate = function (radians: number) {
    xform = xform.rotate((radians * 180) / Math.PI);
    return rotate.call(ctx, radians);
  };
  var translate = ctx.translate;
  ctx.translate = function (dx: number, dy: number) {
    xform = xform.translate(dx, dy);
    return translate.call(ctx, dx, dy);
  };
  var transform = ctx.transform;
  ctx.transform = function (a: number, b: number, c: number, d: number, e: number, f: number) {
    var m2 = svg.createSVGMatrix();
    m2.a = a;
    m2.b = b;
    m2.c = c;
    m2.d = d;
    m2.e = e;
    m2.f = f;
    xform = xform.multiply(m2);
    return transform.call(ctx, a, b, c, d, e, f);
  };
  var setTransform = ctx.setTransform;
  ctx.setTransform = function (a: number, b: number, c: number, d: number, e: number, f: number) {
    xform.a = a;
    xform.b = b;
    xform.c = c;
    xform.d = d;
    xform.e = e;
    xform.f = f;
    return setTransform.call(ctx, a, b, c, d, e, f);
  };
  var pt = svg.createSVGPoint();
  ctx.transformedPoint = function (x: number, y: number) {
    pt.x = x;
    pt.y = y;
    return pt.matrixTransform(xform.inverse());
  };
  return ctx as CanvasRenderingContext2DExtra;
}

const Video: React.FunctionComponent<Props> = ({
  size,
  framerate,
  apikey,
  video,
  baseUrl,
  frame,
  index,
  setFrame,
  interactive,
}) => {
  const session = useSession(apikey, video, baseUrl);
  const img = useFrame(session, size, apikey, frame || 1, 250, baseUrl, framerate);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [height, setHeight] = useState(0);
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);
  const [dragged, setDragged] = useState(false);
  const [dragStart, setDragStart] = useState<DOMPoint | null>();
  const [ctx, setCtx] = useState<CanvasRenderingContext2DExtra | null>();

  const redraw = useCallback(() => {
    if (canvasRef && canvasRef.current && containerRef && containerRef.current && img) {
      // get the top left position of the image
      if (img && img.width && img.height) {
        if (canvasRef.current.width !== canvasRef.current.offsetWidth)
          canvasRef.current.width = canvasRef.current.offsetWidth;
        var scale = Math.min(canvasRef.current.width / img.width, canvasRef.current.height / img.height);
        // get the top left position of the image
        var x = canvasRef.current.width / 2 - (img.width / 2) * scale;
        var y = canvasRef.current.height / 2 - (img.height / 2) * scale;
        if (ctx) {
          const p1 = ctx.transformedPoint(0, 0);
          const p2 = ctx.transformedPoint(canvasRef.current.width, canvasRef.current.height);
          ctx.clearRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        }
      }
    }
  }, [img, ctx]);

  const zoom = useCallback(
    (clicks: number) => {
      if (ctx) {
        var pt = ctx.transformedPoint(lastX, lastY);
        ctx.translate(pt.x, pt.y);
        var factor = Math.pow(scaleFactor, clicks);
        ctx.scale(factor, factor);
        ctx.translate(-pt.x, -pt.y);
        redraw();
      }
    },
    [ctx, lastX, lastY, redraw]
  );

  useLayoutEffect(() => {
    if (containerRef && containerRef.current) setHeight(containerRef.current.clientHeight);
  }, [windowWidth, windowHeight]);

  useLayoutEffect(() => {
    if (canvasRef && canvasRef.current) {
      let context = canvasRef.current.getContext('2d');
      let context2 = trackTransforms(context);
      setCtx(context2);
    }
  }, [canvasRef]);

  useLayoutEffect(() => {
    if (canvasRef && canvasRef.current && interactive) {
      let canvas = canvasRef.current;

      const mouseDown = (evt: MouseEvent) => {
        (document.body
          .style as any).mozUserSelect = document.body.style.webkitUserSelect = document.body.style.userSelect = 'none';
        setLastX(evt.offsetX || evt.pageX - canvas.offsetLeft);
        setLastY(evt.offsetY || evt.pageY - canvas.offsetTop);
        if (ctx) setDragStart(ctx.transformedPoint(lastX, lastY));
        setDragged(false);
      };
      canvas.addEventListener('mousedown', mouseDown, false);

      const mouseMove = (evt: MouseEvent) => {
        setLastX(evt.offsetX || evt.pageX - canvas.offsetLeft);
        setLastY(evt.offsetY || evt.pageY - canvas.offsetTop);
        setDragged(true);
        if (dragStart && ctx) {
          var pt = ctx.transformedPoint(lastX, lastY);
          ctx.translate(pt.x - dragStart.x, pt.y - dragStart.y);
          redraw();
        }
      };
      canvas.addEventListener('mousemove', mouseMove, false);

      const mouseUp = () => {
        setDragStart(null);
        // if (!dragged) zoom(evt.shiftKey ? -1 : 1);
      };
      canvas.addEventListener('mouseup', mouseUp, false);

      const handleScroll = (evt: WheelEvent) => {
        if (evt.shiftKey) {
          var delta = evt.deltaY ? evt.deltaY / 40 : evt.detail ? -evt.detail : 0;
          if (delta) zoom(delta);
          evt.preventDefault();
        } else if (evt.altKey) {
          let newFrame = Math.floor((frame ? frame : 1) + evt.deltaY / PIXELS_PER_FRAME);
          if (newFrame < 1) {
            newFrame = 1;
          }
          setFrame(index, newFrame);
          evt.preventDefault();
        }
        return false;
      };
      canvas.addEventListener('wheel', handleScroll, false);
      return () => {
        canvas.removeEventListener('wheel', handleScroll);
        canvas.removeEventListener('mouseup', mouseUp);
        canvas.removeEventListener('mousedown', mouseDown);
        canvas.removeEventListener('mousemove', mouseMove);
      };
    }
  }, [canvasRef, ctx, dragStart, dragged, lastX, lastY, redraw, zoom, frame, setFrame, index, interactive]);

  useEffect(() => {
    if (frame === undefined) setFrame(index, 1);
  }, [frame, index, setFrame]);

  useEffect(() => {
    if (canvasRef && canvasRef.current) {
      setLastX(canvasRef.current.width / 2);
      setLastY(canvasRef.current.height / 2);
    }
  }, [canvasRef]);

  useEffect(() => {
    redraw();
  }, [frame, canvasRef, windowWidth, windowHeight, redraw]);

  return (
    <Parent>
      <VideoContainer ref={containerRef} tabIndex={index}>
        {img === undefined && interactive && (
          <ProgressContainer>
            <CircularProgress />
          </ProgressContainer>
        )}
        <div style={{ height: height + 'px' }}>
          <StyledCanvas ref={canvasRef} height={height} />
        </div>
      </VideoContainer>
    </Parent>
  );
};

const Parent = styled.div`
  height: 100%;
  position: relative;
`;

type CanvasProps = {
  height: number;
};

const ProgressContainer = styled.div`
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 1;
`;

const StyledCanvas = styled.canvas<CanvasProps>`
  position: sticky;
  height: ${(props) => props.height + 'px'};
  width: 100%;
  top: 0px;
`;

const VideoContainer = styled.div`
  position: relative;
  height: 100%;
`;

(Video as any).whyDidYouRender = true;

export default React.memo(Video);
